import React, { useEffect } from 'react'
import { Grid, Stack, Typography } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'
import Checkbox from 'components/generic/form/Checkbox'
import Input from 'components/generic/form/Input'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import { AUTO_CLOSE_SETTING, CONTRACT_WARNING_SETTING } from 'utils/constants'

interface SettingsAutoCloseWarningProps {
  path: string
}

const SettingsAutoCloseWarning: React.FC<SettingsAutoCloseWarningProps> = ({ path }) => {
  const methods = useFormContext()
  const { control, setValue, getValues } = methods

  const autoCloseSetting = useWatch({ control, name: `${path}.autoCloseSetting` })
  const warningValue = useWatch({ control, name: `${path}.warningSetting` })

  // Determine visibility and behavior based on the dropdown selection
  const isVisible = autoCloseSetting !== AUTO_CLOSE_SETTING.OFF
  const isFulfillmentBased =
    autoCloseSetting === AUTO_CLOSE_SETTING.CLOSE_ON_FULFILLMENT ||
    autoCloseSetting === AUTO_CLOSE_SETTING.WARN_ON_FULFILLMENT
  const isExpirationBased =
    autoCloseSetting === AUTO_CLOSE_SETTING.CLOSE_ON_EXPIRATION ||
    autoCloseSetting === AUTO_CLOSE_SETTING.WARN_ON_EXPIRATION
  const isWarningOption =
    autoCloseSetting === AUTO_CLOSE_SETTING.WARN_ON_FULFILLMENT ||
    autoCloseSetting === AUTO_CLOSE_SETTING.WARN_ON_EXPIRATION
  const thresholdPath = isFulfillmentBased
    ? `${path}.fulfillmentWarningThreshold`
    : `${path}.expirationWarningThreshold`

  const isWarningOn =
    (isFulfillmentBased && warningValue === CONTRACT_WARNING_SETTING.FULFILLMENT_WARNING) ||
    (isExpirationBased && warningValue === CONTRACT_WARNING_SETTING.EXPIRATION_WARNING)

  const handleWarningChange = (checked: boolean) => {
    if (checked) {
      if (isFulfillmentBased) {
        setValue(`${path}.warningSetting`, CONTRACT_WARNING_SETTING.FULFILLMENT_WARNING, { shouldDirty: true })
      } else if (isExpirationBased) {
        setValue(`${path}.warningSetting`, CONTRACT_WARNING_SETTING.EXPIRATION_WARNING, { shouldDirty: true })
      }
    } else {
      setValue(`${path}.warningSetting`, CONTRACT_WARNING_SETTING.OFF, { shouldDirty: true })
    }
  }

  // Set warning value based on warning options
  useEffect(() => {
    if (isWarningOption) {
      if (autoCloseSetting === AUTO_CLOSE_SETTING.WARN_ON_FULFILLMENT) {
        setValue(`${path}.warningSetting`, CONTRACT_WARNING_SETTING.FULFILLMENT_WARNING)
      } else if (autoCloseSetting === AUTO_CLOSE_SETTING.WARN_ON_EXPIRATION) {
        setValue(`${path}.warningSetting`, CONTRACT_WARNING_SETTING.EXPIRATION_WARNING)
      }
    } else if (autoCloseSetting === AUTO_CLOSE_SETTING.OFF) {
      setValue(`${path}.warningSetting`, CONTRACT_WARNING_SETTING.OFF)
    }
  }, [isWarningOption, autoCloseSetting, path, setValue])

  useEffect(() => {
    if (isFulfillmentBased) {
      const currentValue = getValues(`${path}.fulfillmentWarningThreshold`)
      if (currentValue === undefined) {
        setValue(`${path}.fulfillmentWarningThreshold`, 90, { shouldDirty: true })
      }
    } else if (isExpirationBased) {
      const currentValue = getValues(`${path}.expirationWarningThreshold`)
      if (currentValue === undefined) {
        setValue(`${path}.expirationWarningThreshold`, 4, { shouldDirty: true })
      }
    }
  }, [autoCloseSetting, path, setValue, getValues, isFulfillmentBased, isExpirationBased])

  if (!isVisible) {
    return null
  }

  return (
    <Grid mt={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Checkbox
          name=""
          label=""
          control={control}
          checked={isWarningOn}
          onChange={e => handleWarningChange(e.target.checked)}
          disabled={isWarningOption}
          noMargin
        />
        <Typography variant="body2" color="textSecondary">
          {isFulfillmentBased ? 'Show alert when contract is' : 'Show alert when contract is closing in'}
        </Typography>
        <Input
          key={thresholdPath}
          name={thresholdPath}
          control={control}
          size="small"
          sx={{ maxWidth: '80px' }}
          required={isWarningOn}
          defaultValue={isFulfillmentBased ? 90 : 4}
          InputProps={{
            inputComponent: InputFormat,
            inputProps: {
              inputType: isFulfillmentBased ? 'percent' : 'number',
              allowLeadingZeros: false,
              allowNegative: false,
              thousandSeparator: false,
              decimalScale: 0,
              ...(isFulfillmentBased && {
                isAllowed: values => {
                  const { floatValue } = values
                  return floatValue === undefined || floatValue <= 100
                },
              }),
            },
          }}
        />
        <Typography variant="body2" color="textSecondary">
          {isFulfillmentBased ? 'fulfilled' : 'days'}
        </Typography>
      </Stack>
    </Grid>
  )
}

export default SettingsAutoCloseWarning
